import React from 'react';
import { CheckIcon, SwitchHorizontalIcon } from '@heroicons/react/outline';
import Avatar from '../Avatar';
import { useProviderContext } from '../../hooks/useProviderContext';
import classNames from 'classnames';

type Provider = {
  id: string;
  name: string;
  squareLogoMedia?: { url: string } | null;
};

const ProviderItem: React.FC<{
  provider: Provider;
  isActive: boolean;
  onSwitch: () => void;
}> = ({ provider, isActive, onSwitch }) => (
  <button
    type="button"
    className={classNames(
      'flex w-full items-center gap-3 px-4 py-2 text-left text-caption focus:outline-none',
      isActive
        ? 'cursor-default bg-neutral-50 text-neutral-150'
        : 'cursor-pointer text-neutral-125 hover:bg-neutral-50',
    )}
    onClick={isActive ? undefined : onSwitch}
  >
    <Avatar
      size="small"
      name={provider.name}
      imageUrl={provider.squareLogoMedia?.url}
      className="flex-shrink-0"
    />
    <span className="flex-grow truncate">{provider.name}</span>
    {isActive ? (
      <CheckIcon className="h-4 w-4 flex-shrink-0 text-green-100" />
    ) : (
      <SwitchHorizontalIcon className="h-4 w-4 flex-shrink-0 text-neutral-125" />
    )}
  </button>
);

const ProviderContexts: React.FC = () => {
  const {
    availableContexts,
    loading,
    handleContextSwitch,
    activeProviderId,
    primaryProviderId,
    isEnabled,
  } = useProviderContext();

  if (!isEnabled || loading || availableContexts.length <= 1) {
    return null;
  }

  const primaryProvider = primaryProviderId
    ? availableContexts.find((p) => p.id === primaryProviderId)
    : null;
  const collaborateProviders = availableContexts.filter(
    (p) => p.id !== primaryProviderId,
  );

  return (
    <div className="border-t border-neutral-75 pt-2">
      {primaryProvider && (
        <div className="pb-2">
          <div className="px-4 py-2 text-small-caption font-medium text-neutral-150">
            Your organization
          </div>
          <ProviderItem
            provider={primaryProvider}
            isActive={primaryProvider.id === activeProviderId}
            onSwitch={() => handleContextSwitch(primaryProvider.id)}
          />
        </div>
      )}
      {collaborateProviders.length > 0 && (
        <div
          className={primaryProvider ? 'border-t border-neutral-75 pt-2' : ''}
        >
          <div className="px-4 py-2 text-small-caption font-medium text-neutral-125">
            Collaborating with
          </div>
          {collaborateProviders.map((provider) => (
            <ProviderItem
              key={provider.id}
              provider={provider}
              isActive={provider.id === activeProviderId}
              onSwitch={() => handleContextSwitch(provider.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProviderContexts;
